import { getCurrentApi } from './api';
import { logUserAction, getAuditLogs, getUserAuditLogs, FirebaseAuditLog } from './firebaseService';
import { getCurrentUser } from './authService';

export interface AuditLog {
  id: number;
  timestamp: string;
  category: string;
  categoryDisplayName: string;
  actionType: string;
  actorType: string;
  actorDisplayName: string;
  username?: string;
  systemComponent?: string;
  zoneId?: number;
  zoneName?: string;
  actionDescription: string;
  previousValue?: string;
  newValue?: string;
  additionalInfo?: string;
  ipAddress: string;
  durationMs?: number;
  success: boolean;
  formattedMessage: string;
}

export interface AuditLogResponse {
  logs: AuditLog[];
  totalCount: number;
  skip: number;
  take: number;
}

export interface AuditLogFilters {
  skip?: number;
  take?: number;
  userId?: number;
  zoneId?: number;
  category?: string;
  actorType?: string;
  fromDate?: string;
  toDate?: string;
}

export interface AuditStatistics {
  todayUserActions: number;
  todaySystemOperations: number;
  weekUserActions: number;
  weekSystemOperations: number;
  totalUserActions: number;
  totalSystemOperations: number;
}

export interface AuditCategory {
  value: number;
  name: string;
  displayName: string;
}

export interface AuditActorType {
  value: number;
  name: string;
  displayName: string;
}

class AuditService {
  /**
   * Log user action to Firebase
   */
  async logAction(action: string, details?: {
    zoneId?: number;
    zoneName?: string;
    previousValue?: string;
    newValue?: string;
    additionalInfo?: string;
  }): Promise<void> {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser || !currentUser.hotelId) return;

      const formattedMessage = this.formatActionMessage(action, currentUser.username, details);

      await logUserAction({
        hotelId: currentUser.hotelId,
        userId: currentUser.id,
        username: currentUser.username,
        action,
        actionType: this.determineActionType(action),
        zoneId: details?.zoneId,
        zoneName: details?.zoneName,
        previousValue: details?.previousValue,
        newValue: details?.newValue,
        additionalInfo: details?.additionalInfo,
        success: true,
        formattedMessage
      });
    } catch (error) {
      console.error('Error logging action to Firebase:', error);
    }
  }

  /**
   * Get user action logs for the "User Actions" tab - Firebase version
   */
  async getUserActionLogs(filters: AuditLogFilters = {}): Promise<AuditLogResponse> {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser || !currentUser.hotelId) {
        return { logs: [], totalCount: 0, skip: 0, take: 100 };
      }
      
      const firebaseLogs = await getAuditLogs(currentUser.hotelId, filters.take || 100);
      
      // Convert Firebase logs to expected format
      const logs: AuditLog[] = firebaseLogs.map(log => ({
        id: parseInt(log.id || '0'),
        timestamp: log.timestamp.toISOString(),
        category: 'UserAction',
        categoryDisplayName: 'Kullanıcı İşlemi',
        actionType: log.actionType,
        actorType: 'User',
        actorDisplayName: 'Kullanıcı',
        username: log.username,
        zoneId: log.zoneId,
        zoneName: log.zoneName,
        actionDescription: log.action,
        previousValue: log.previousValue,
        newValue: log.newValue,
        additionalInfo: log.additionalInfo,
        ipAddress: log.ipAddress || '',
        success: log.success,
        formattedMessage: log.formattedMessage
      }));

      return {
        logs,
        totalCount: logs.length,
        skip: filters.skip || 0,
        take: filters.take || 100
      };
    } catch (error) {
      console.error('Error fetching user action logs from Firebase:', error);
      return {
        logs: [],
        totalCount: 0,
        skip: 0,
        take: 100
      };
    }
  }

  /**
   * Get system operation logs for the "System Logs" tab - Empty for now (Firebase focused on user actions)
   */
  async getSystemOperationLogs(filters: AuditLogFilters = {}): Promise<AuditLogResponse> {
    return {
      logs: [],
      totalCount: 0,
      skip: filters.skip || 0,
      take: filters.take || 100
    };
  }

  /**
   * Get available audit categories for filtering
   */
  async getCategories(): Promise<AuditCategory[]> {
    return [
      { value: 1, name: 'UserAction', displayName: 'Kullanıcı İşlemi' }
    ];
  }

  /**
   * Get available actor types for filtering
   */
  async getActorTypes(): Promise<AuditActorType[]> {
    return [
      { value: 1, name: 'User', displayName: 'Kullanıcı' }
    ];
  }

  /**
   * Get audit statistics for dashboard
   */
  async getStatistics(): Promise<AuditStatistics> {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser || !currentUser.hotelId) {
        return {
          todayUserActions: 0,
          todaySystemOperations: 0,
          weekUserActions: 0,
          weekSystemOperations: 0,
          totalUserActions: 0,
          totalSystemOperations: 0
        };
      }
      
      const logs = await getAuditLogs(currentUser.hotelId, 1000); // Son 1000 log
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todayLogs = logs.filter(log => log.timestamp >= today);
      
      return {
        todayUserActions: todayLogs.length,
        todaySystemOperations: 0,
        weekUserActions: logs.length, // Basitleştirilmiş
        weekSystemOperations: 0,
        totalUserActions: logs.length,
        totalSystemOperations: 0
      };
    } catch (error) {
      return {
        todayUserActions: 0,
        todaySystemOperations: 0,
        weekUserActions: 0,
        weekSystemOperations: 0,
        totalUserActions: 0,
        totalSystemOperations: 0
      };
    }
  }

  private formatActionMessage(action: string, username: string, details?: any): string {
    let message = `${username} - ${action}`;
    
    if (details?.zoneName) {
      message += ` (Zone: ${details.zoneName})`;
    }
    
    if (details?.previousValue && details?.newValue) {
      message += ` [${details.previousValue} → ${details.newValue}]`;
    }
    
    return message;
  }

  private determineActionType(action: string): string {
    if (action.includes('start') || action.includes('play')) return 'ZoneStarted';
    if (action.includes('stop')) return 'ZoneStopped';
    if (action.includes('volume')) return 'VolumeChanged';
    if (action.includes('next')) return 'TrackSkipped';
    if (action.includes('previous')) return 'TrackPrevious';
    return 'ZoneConfigChanged';
  }
}

export const auditService = new AuditService();